import { c as _c } from "react/compiler-runtime";
import React, { useCallback, useRef } from 'react';
import { DropdownList, DropdownListProps } from '../../dropdown/DropdownList/DropdownList';
import { DropdownPopper, DropdownPopperProps } from '../../dropdown/DropdownPopper/DropdownPopper';
import { useCombinedEventCallbacks } from '../../hooks/useCombinedEventCallbacks';
import { useEventCallback } from '../../hooks/useEventCallback';
import { IconDeprecated, IconType } from '../../misc/IconDeprecated/IconDeprecated';
import { prepareProps } from '../../utils/propUtils';
import { classes } from '../../utils/utils';
import { InputLayout, InputLayoutProps } from '../InputLayout/InputLayout';
import sharedStyles from '../sharedStyles.module.css';
import styles from './SearchableSelect.module.css';
import { useHighlightedSelectOption } from './useHighlightedSelectOption';
import { useSelectDropdownVisible } from './useSelectDropdownVisible';
import { getDisplayTextOfSelectOption, getKeyOfSelectOption, SelectOption, useSelectOptions } from './useSelectOptions';
export type SearchableSelectProps<T> = Omit<InputLayoutProps, 'children'> & Pick<DropdownListProps<T>, 'getKeyOfItem' | 'getDisplayTextOfItem' | 'items'> & {
  readonly value: T;
  readonly onValueChanged: (value: T) => void;
};
export const SearchableSelect = React.memo(props => {
  const $ = _c(39);
  let t0;
  if ($[0] !== props) {
    t0 = prepareProps({
      props,
      ignoreChildren: true
    });
    $[0] = props;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  let getDisplayTextOfItem;
  let getKeyOfItem;
  let items;
  let label;
  let onValueChanged;
  let remainingProps;
  let value;
  if ($[2] !== t0) {
    ({
      value,
      items,
      getKeyOfItem,
      getDisplayTextOfItem,
      onValueChanged,
      label,
      ...remainingProps
    } = t0);
    $[2] = t0;
    $[3] = getDisplayTextOfItem;
    $[4] = getKeyOfItem;
    $[5] = items;
    $[6] = label;
    $[7] = onValueChanged;
    $[8] = remainingProps;
    $[9] = value;
  } else {
    getDisplayTextOfItem = $[3];
    getKeyOfItem = $[4];
    items = $[5];
    label = $[6];
    onValueChanged = $[7];
    remainingProps = $[8];
    value = $[9];
  }
  const inputElementRef = useRef(null);
  const dropdownElementRef = useRef(null);
  let t1;
  if ($[10] !== getDisplayTextOfItem || $[11] !== getKeyOfItem || $[12] !== items || $[13] !== value) {
    t1 = {
      value,
      items,
      getKeyOfItem,
      getDisplayTextOfItem
    };
    $[10] = getDisplayTextOfItem;
    $[11] = getKeyOfItem;
    $[12] = items;
    $[13] = value;
    $[14] = t1;
  } else {
    t1 = $[14];
  }
  const {
    options,
    currentOption
  } = useSelectOptions(t1);
  let t2;
  if ($[15] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {
      inputElementRef,
      dropdownElementRef
    };
    $[15] = t2;
  } else {
    t2 = $[15];
  }
  const {
    dropdownVisible,
    handleInputClick,
    handleInputKeydown: handleInputKeydownDropdownVisible,
    handleOptionSelected: handleOptionSelectedDropdownVisible
  } = useSelectDropdownVisible(t2);
  let t3;
  if ($[16] !== handleOptionSelectedDropdownVisible || $[17] !== onValueChanged || $[18] !== value) {
    t3 = option => {
      if (option.item !== value) {
        onValueChanged(option.item);
      }
      handleOptionSelectedDropdownVisible();
    };
    $[16] = handleOptionSelectedDropdownVisible;
    $[17] = onValueChanged;
    $[18] = value;
    $[19] = t3;
  } else {
    t3 = $[19];
  }
  const handleOptionSelected = useEventCallback(t3);
  let t4;
  if ($[20] !== currentOption || $[21] !== dropdownVisible || $[22] !== handleOptionSelected || $[23] !== options) {
    t4 = {
      options,
      currentOption,
      active: dropdownVisible,
      onOptionSelected: handleOptionSelected
    };
    $[20] = currentOption;
    $[21] = dropdownVisible;
    $[22] = handleOptionSelected;
    $[23] = options;
    $[24] = t4;
  } else {
    t4 = $[24];
  }
  const {
    highlightedOption,
    handleInputKeydown: handleInputKeydownHighlight
  } = useHighlightedSelectOption(t4);
  const handleInputKeydown = useCombinedEventCallbacks(handleInputKeydownDropdownVisible, handleInputKeydownHighlight);
  let t5;
  if ($[25] !== handleOptionSelected || $[26] !== highlightedOption || $[27] !== options) {
    t5 = t6 => {
      const {
        ref
      } = t6;
      return <DropdownList ref={ref} items={options} highlightedItem={highlightedOption ?? undefined} getKeyOfItem={getKeyOfSelectOption} getDisplayTextOfItem={getDisplayTextOfSelectOption} onItemSelected={handleOptionSelected} data-testid="SearchableSelect.DropdownList" />;
    };
    $[25] = handleOptionSelected;
    $[26] = highlightedOption;
    $[27] = options;
    $[28] = t5;
  } else {
    t5 = $[28];
  }
  const renderPopperContent = t5;
  let t6;
  if ($[29] !== currentOption.displayText || $[30] !== dropdownVisible || $[31] !== handleInputClick || $[32] !== handleInputKeydown || $[33] !== renderPopperContent) {
    t6 = () => <DropdownPopper visible={dropdownVisible} renderContent={renderPopperContent} childElementRef={inputElementRef} contentElementRef={dropdownElementRef}>{t7 => {
        const {
          ref: ref_0
        } = t7;
        return <div className={styles.inputAndIconWrapper}><input ref={ref_0} type="text" className={classes(sharedStyles.inputBaseStyle, styles.input)} value={currentOption.displayText} onClick={handleInputClick} onKeyDown={handleInputKeydown} data-testid="SearchableSelect.Input" /><div className={styles.iconContainer}><IconDeprecated name="caret-down" type={IconType.Solid} /></div></div>;
      }}</DropdownPopper>;
    $[29] = currentOption.displayText;
    $[30] = dropdownVisible;
    $[31] = handleInputClick;
    $[32] = handleInputKeydown;
    $[33] = renderPopperContent;
    $[34] = t6;
  } else {
    t6 = $[34];
  }
  const renderInput = t6;
  let t7;
  if ($[35] !== label || $[36] !== remainingProps || $[37] !== renderInput) {
    t7 = <InputLayout label={label} {...remainingProps}>{renderInput}</InputLayout>;
    $[35] = label;
    $[36] = remainingProps;
    $[37] = renderInput;
    $[38] = t7;
  } else {
    t7 = $[38];
  }
  return t7;
}) as <T>(props: SearchableSelectProps<T>) => React.JSX.Element;